import axios from 'axios';
import firebase from 'firebase/app';
import 'firebase/firestore';
import Vue from 'vue';
import Vuex from 'vuex';
import router from '../router';
import { ctr } from '../util/ctr';
import notify from '../util/notification';
import tokenManagerService from '../services/tokenManager';
import { managerService } from '../services/manager.service';
import { templateService } from '../services/templateService';

// Import Vuex modules
import userModule from './modules/user';
import retentionModule from './modules/retention';
import appModule from './modules/app';

// Legacy function name - now uses access tokens instead of ID tokens
// Access tokens are used for API authentication and contain authorization information (groups)
const requestWithIdToken = async (callback, resolve, reject) => {
	try {
		// Use access token for API calls (backend now expects access tokens)
		const accessToken = await tokenManagerService.getValidToken();
		console.log('✅ Access Token retrieved - Token exists:', !!accessToken);
		
		if (accessToken) {
			callback(accessToken, resolve, reject);
		} else {
			// Token manager handles authentication redirect internally
			console.log("⚠️ No valid access token available");
			reject(new Error('Authentication required'));
		}
	} catch (error) {
		console.error("❌ Error during access token request:", error);
		reject(error);
	}
};

const isLocal = window.location.href.indexOf('localhost') >= 0;
const configManagerUrl = isLocal ? 'http://localhost:5001/manager' : '/manager';
const backendUrl = isLocal ? 'http://localhost:5002' : '';
const isStaging = window.location.host.indexOf('staging') >= 0 || window.location.host.indexOf('dev') >= 0 || isLocal;

Vue.use(Vuex);

const store = new Vuex.Store({
	modules: {
		user: userModule,
		retention: retentionModule,
		app: appModule
	},
	state: {
		parameters: [],
		islKeys: [],
		conditionTypes: [],
		configManagerUrl,
		pushNotifApiKey: 'RNtVPIPGSYDhFbxiS68K',
		backendUrl,
		audienceOptions: [],
		audiences: [],
		experiments: [],
		allExperiments: [],
		messages: [],
		templates: [],
			// Looker folders + per-folder template looks for the active app
			lookerConfiguration: { folders: {} },
		permissionTypes: [],
		isStaging,
		// Cache for UserCheck page so navigating away and back preserves results
		userCheckCache: null,
		// Holds Firestore unsubscribe functions per messageId for pending listeners
		pendingMessageUnsubscribes: {},
		loading: {
			entities: {},
			initialized: false
		}
	},
	mutations: {
		SET_PARAMETERS(state, payload) {
			state.parameters = payload.sort((a, b) => {
				const aDate = a.created_at || 0;
				const bDate = b.created_at || 0;
				return bDate - aDate;
			});
		},
		SET_ISL_KEYS(state, payload) {
			state.islKeys = payload.sort((a, b) => {
				const aDate = a.created_at || 0;
				const bDate = b.created_at || 0;
				return bDate - aDate;
			});
		},
		SET_CONDITION_TYPES(state, payload) {
			state.conditionTypes = payload.sort((a, b) => {
				const aKey = (a.key || '').toUpperCase();
				const bKey = (b.key || '').toUpperCase();
				return aKey.localeCompare(bKey);
			});
		},
		SET_AUDIENCE_OPTIONS(state, payload) {
			state.audienceOptions = payload.sort((a, b) => {
				const aKey = (a.key || '').toUpperCase();
				const bKey = (b.key || '').toUpperCase();
				return aKey.localeCompare(bKey);
			});
		},
		SET_AUDIENCES(state, payload) {
			state.audiences = payload;
		},
		SET_EXPERIMENTS(state, payload) {
			state.experiments = payload.sort((a, b) => {
				const aDate = a.create_date || 0;
				const bDate = b.create_date || 0;
				return bDate - aDate;
			});
		},
		SET_ALL_EXPERIMENTS(state, payload) {
			state.allExperiments = payload;
		},
		SET_MESSAGES(state, payload) {
			state.messages = payload;
		},
		UPDATE_MESSAGE_BY_ID(state, updated) {
			if (!updated || !updated.id) return;
			const idx = state.messages.findIndex(m => m.id === updated.id);
			if (idx === -1) return;
			const merged = { ...state.messages[idx], ...updated };
			merged.id = state.messages[idx].id;
			Vue.set(state.messages, idx, merged);
		},
		SET_PENDING_MESSAGE_UNSUBSCRIBE(state, { messageId, fn }) {
			if (!messageId) return;
			if (fn) Vue.set(state.pendingMessageUnsubscribes, messageId, fn);
			else Vue.delete(state.pendingMessageUnsubscribes, messageId);
		},
		SET_TEMPLATES(state, templates) {
			state.templates = templates;
		},
		SET_LOOKER_CONFIGURATION(state, payload) {
			state.lookerConfiguration = payload || { folders: {} };
		},
		SET_PERMISSION_TYPES(state, payload) {
			state.permissionTypes = payload;
		},
		SET_PERMISSION_TYPES(state, payload) {
			state.permissionTypes = payload;
		},
		SET_LOADING(state, { type, value }) {
			state.loading.entities[type] = value;
		},
		SET_INITIALIZED(state, value) {
			state.loading.initialized = value;
		},
		SET_USER_CHECK_CACHE(state, payload) {
			state.userCheckCache = payload;
		},
		SET_PERMISSIONS(state, payload) {
			state.permissions.loaded = true;
			state.permissions.permissions = payload.permissions || [];
			state.permissions.authContext = payload.authContext;
			state.permissions.capabilities = payload.capabilities || {};
		},
		SET_PERMISSIONS_LOADING(state, value) {
			state.permissions.loaded = !value;
		},
		RESET_PERMISSIONS(state) {
			state.permissions.loaded = false;
			state.permissions.permissions = [];
			state.permissions.authContext = null;
			state.permissions.capabilities = {
				canEditExperiments: false,
				canEditOngoingExperiments: false,
				canSendPushNotifications: false,
				canSchedulePushNotifications: false,
				canCreateDraftMessages: false,
				canDeleteExperiments: false,
				canActivateExperiments: false,
				canViewISLKeys: false,
				canCreateISLKeys: false,
				canEditISLKeys: false,
				canDeleteISLKeys: false,
				isAdmin: false,
				canViewRetentionMessages: false,
				canCreateRetentionMessages: false,
				canEditRetentionMessages: false,
				canDeleteRetentionMessages: false,
				canApproveRetentionMessages: false
			};
		}
	},
	actions: {
		async fetchApps({ dispatch, rootGetters }) {
			// Delegate to app module
			await dispatch('app/fetchApps', null, { root: true });
			
			// Only set active app if explicitly provided in route
			const currentRoute = router.currentRoute;
			const hasAppIdInRoute = currentRoute && currentRoute.params && currentRoute.params.aid;
			const activeApp = rootGetters['app/activeApp'];
			
			if (hasAppIdInRoute && !activeApp) {
				// If we have an app ID in the route but no activeApp set, use the route's app ID
				console.log('Setting active app from route:', currentRoute.params.aid);
				dispatch('setActiveApp', currentRoute.params.aid);
			} else if (hasAppIdInRoute && activeApp) {
				console.log('Active app already set to:', activeApp, 'from route:', currentRoute.params.aid);
			} else if (!hasAppIdInRoute) {
				console.log('No app ID in route - user should be on homepage or redirected there');
			}
		},
		async setUser({ commit, dispatch }) {
			return dispatch('user/setUser', null, { root: true });
		},
		fetchAllConfig({ dispatch }) {
			const configTypes = ['parameter', 'isl', 'audience', 'experiment', 'message', 'template', 'retention-message'];
			
			configTypes.forEach((item) => {
				dispatch('configFetch', item).catch(err => {
					console.error(`Error fetching ${item}:`, err);
				});
			});
			
			dispatch('allExperimentFetch').catch(err => {
				console.error('Error fetching all experiments:', err);
			});
			
			dispatch('fetchAudienceOptions').catch(err => {
				console.error('Error fetching audience options:', err);
			});

			dispatch('fetchLookerConfiguration').catch(err => {
				console.error('Error fetching looker configuration:', err);
			});
		},
		async fetchLookerConfiguration({ commit, rootGetters }) {
			try {
				const activeApp = rootGetters['app/activeApp'];
				const res = await managerService.getLookerConfiguration({ aid: activeApp });
				commit('SET_LOOKER_CONFIGURATION', res.data.result);
			} catch (err) {
				console.error('Error fetching looker configuration:', err);
				commit('SET_LOOKER_CONFIGURATION', { folders: {} });
			}
		},
		async configFetch({ commit, state, rootGetters }, type) {
			try {
				console.log(state.configManagerUrl, 'fetching from');
				const commits = {
					parameter: 'SET_PARAMETERS',
					isl: 'SET_ISL_KEYS',
					audience: 'SET_AUDIENCES',
					experiment: 'SET_EXPERIMENTS',
					message: 'SET_MESSAGES',
					template: 'SET_TEMPLATES',
					'retention-message': 'SET_RETENTION_MESSAGES'
				};

				if (type === 'retention-message') {
					await this.dispatch('retention/fetchMessages', null, { root: true });
					return;
				}

				const activeApp = rootGetters['app/activeApp'];
				const res = await managerService.getItems(type, { aid: activeApp });
				commit(commits[type], res.data.result);
				
				if (type === 'message') {
					const list = res.data.result || [];
					const pendingIds = new Set(list.filter(m => m && m.audience_count && m.audience_count.status === 'PENDING').map(m => m.id));
					
					if (pendingIds.size > 0) {
						console.log(`[Realtime] Found ${pendingIds.size} pending messages. Starting listeners for IDs:`, Array.from(pendingIds));
					}
					
					pendingIds.forEach(id => {
						this.dispatch('startPendingMessageListener', { appId: activeApp, messageId: id });
					});
					
					Object.keys(state.pendingMessageUnsubscribes || {}).forEach(id => {
						if (!pendingIds.has(id)) {
							this.dispatch('stopPendingMessageListener', { messageId: id });
						}
					});
				}
			} catch (error) {
				console.error(`Error in configFetch for ${type}:`, error);
				throw error;
			}
		},
		async allExperimentFetch({ commit, rootGetters }) {
			try {
				const activeApp = rootGetters['app/activeApp'];
				const res = await managerService.getAllExperiments({ aid: activeApp });
				commit('SET_ALL_EXPERIMENTS', res.data.result);
			} catch (err) {
				console.error("Error occurred during getting all experiments from all apps:", err);
			}
		},
		configAdd({ state, dispatch }, { type, payload }) {
			return new Promise((resolve, reject) => {
				managerService.configAdd(type,payload)
						.then((res) => {
							dispatch('configFetch', type);
							if (res.status === 200) {
								notify(type + ' added successfully!', 'success');
								resolve(res);
							} else {
								notify(type + "couldn't be added.", 'error');
								reject(res);
							}
						})
						.catch((err) => {
							notify(type + "couldn't be edited.", 'error');
							console.log(err);
							reject(err);
						});
				});
		},
		configEdit({ state, dispatch }, { type, payload }) {
			return new Promise((resolve, reject) => {
				managerService.configEdit(type,payload)
							.then((res) => {
								if (res.status === 200) {
									notify(type + ' edited successfully!', 'success');
								} else {
									notify(type + " couldn't be edited. ", 'error');
								}
								resolve(res);
								dispatch('configFetch', type);
							})
							.catch((err) => {
								let errorMessage = '';
								if (err.response.data) errorMessage = err.response.data.errorMessage;
								notify(type + " couldn't be edited. " + errorMessage, 'error');
								reject(err);
							});
					},
				)
		},
		configDelete({ state, dispatch }, { type, payload }) {
			managerService.configDelete(type,payload)
					.then((res) => {
						if (res.status === 200) {
							notify(type + ' deleted successfully!', 'success');
						} else {
							notify(type + "couldn't be deleted.", 'error');
						}
						dispatch('configFetch', type);
					});
		},
		activationOp({ state, dispatch }, payload) {
			managerService.activationOp(payload)
				.then((res) => {
						dispatch('configFetch', payload.type);
				});
			
		},
		// --- REALTIME (FIRESTORE) LISTENERS FOR PENDING MESSAGES ---
		startPendingMessageListener({ state, commit, dispatch }, { appId, messageId }) {
			try {
				if (!firebase || !firebase.apps || firebase.apps.length === 0 || typeof firebase.firestore !== 'function') {
					return; // Firebase not initialized
				}
				// If already listening to this message, skip
				if (state.pendingMessageUnsubscribes && state.pendingMessageUnsubscribes[messageId]) return;

				console.log(`[Realtime] Attaching listener for messageId: ${messageId}`);

				const db = firebase.firestore();
				const collectionName = state.isStaging ? 'messages-staging' : 'messages';
				const docRef = db.collection('config').doc(appId).collection(collectionName).doc(messageId);
				const unsub = docRef.onSnapshot((snap) => {
					if (!snap.exists) {
						console.warn(`[Realtime] MessageId ${messageId} no longer exists. Cleaning up listener.`);
						dispatch('stopPendingMessageListener', { messageId });
						return;
					}
					const data = snap.data() || {};
					console.log(`[Realtime] Received update for messageId: ${messageId}`, data);
					const updated = { id: messageId, ...data };
					commit('UPDATE_MESSAGE_BY_ID', updated);
					const status = data && data.audience_count && data.audience_count.status;
					if (status && status !== 'PENDING') {
						console.log(`[Realtime] MessageId ${messageId} is no longer PENDING (status: ${status}). Cleaning up listener.`);
						// Stop listening once pending resolved for this message
						dispatch('stopPendingMessageListener', { messageId });
					}
				}, (err) => {
					console.error(`[Realtime] Error on listener for messageId: ${messageId}`, err);
					dispatch('stopPendingMessageListener', { messageId });
				});

				commit('SET_PENDING_MESSAGE_UNSUBSCRIBE', { messageId, fn: unsub });
			} catch (_e) {
				// noop
			}
		},
		stopPendingMessageListener({ state, commit }, { messageId } = {}) {
			if (messageId) {
				const fn = state.pendingMessageUnsubscribes && state.pendingMessageUnsubscribes[messageId];
				if (fn) {
					console.log(`[Realtime] Detaching listener for single messageId: ${messageId}`);
					try { fn(); } catch (_e) {}
				}
				commit('SET_PENDING_MESSAGE_UNSUBSCRIBE', { messageId, fn: null });
				return;
			}
			// Stop all listeners
			const allIds = Object.keys(state.pendingMessageUnsubscribes || {});
			if (allIds.length > 0) {
				console.log(`[Realtime] Detaching all listeners for message IDs:`, allIds);
			}
			Object.keys(state.pendingMessageUnsubscribes || {}).forEach(id => {
				const fn = state.pendingMessageUnsubscribes[id];
				if (fn) { try { fn(); } catch (_e) {} }
				commit('SET_PENDING_MESSAGE_UNSUBSCRIBE', { messageId: id, fn: null });
			});
		},
		setActiveApp({ dispatch, rootGetters }, payload) {
			// Delegate to app module, but still handle fetchAllConfig at root level
			dispatch('app/setActiveApp', payload, { root: true });
			
			// Fetch config if user is authenticated
			const user = rootGetters['user/user'];
			if (user) {
				console.log("Dispatching fetchAllConfig for app:", payload);
				dispatch('fetchAllConfig');
			} else {
				console.log("User not authenticated yet, skipping fetchAllConfig");
			}
		},
		fetchAudienceOptions({ rootGetters, commit }) {
			return requestWithIdToken((idToken) => {
				const activeApp = rootGetters['app/activeApp'];
				return axios
					.get(backendUrl + '/audience/options', {
						headers: { authorization: idToken },
						params: { aid: activeApp }
					})
					.then((res) => {
						commit('SET_AUDIENCE_OPTIONS', res.data);
					})
					.catch((err) => {
						console.error('Error fetching audience options:', err);
						throw err;
					});
			}).catch((err) => {
				console.error('Error in requestWithIdToken for audience options:', err);
			});
		},
		async fetchUserPermissions({ commit }) {
			try {
				commit('SET_PERMISSIONS_LOADING', true);
				const permissionService = await import('../services/permission.service');
				await permissionService.default.fetchUserPermissions();
				const permissionContext = permissionService.default.getPermissionContext();
				
				commit('SET_PERMISSIONS', permissionContext);
				return permissionContext;
			} catch (error) {
				console.error('Error fetching user permissions:', error);
				commit('RESET_PERMISSIONS');
				throw error;
			}
		},
		async fetchPermissionTypes({ commit }) {
			try {
				const { permissionTypeService } = await import('../services/permissionType.service');
				const response = await permissionTypeService.getAllPermissionTypes();
				commit('SET_PERMISSION_TYPES', response.data.result);
			} catch (error) {
				console.error('Error fetching permission types:', error);
			}
		}
	},
	getters: {
		// Delegate user getter to module
		user: (state, getters) => getters['user/user'],
		parameters(state) {
			return state.parameters;
		},
		allExperiments(state) {
			return state.allExperiments;
		},
		islKeys(state) {
			return state.islKeys.sort(function(a, b) {
				const aField = a.key ? a.key.toUpperCase() : '';
				const bField = b.key ? b.key.toUpperCase() : '';
				if (aField > bField) {
					return 1;
				} else if (aField < bField) {
					return -1;
				} else {
					return 0;
				}
			});
		},
		pushNotifApiKey(state) {
			return state.pushNotifApiKey;
		},
		configManagerUrl(state) {
			return state.configManagerUrl;
		},
		catalogManagerUrl(state) {
			return state.catalogManagerUrl;
		},
		conditionTypes(state) {
			return state.conditionTypes;
		},
		audienceOptions(state) {
			return state.audienceOptions;
		},
		audiences(state) {
			return state.audiences;
		},
		backendUrl(state) {
			return state.backendUrl;
		},
		experiments(state) {
			return state.experiments;
		},
		allMessageStatistics: (state) => (templateId) => {
			let statistics = {
				sent: 0,
				clicked: 0,
				ctr: 0
			};
			state.messages.forEach((message) => {
				if (message.messages[1]) {
					if (message.messages[0].templateId == templateId) {
						statistics.sent += message.statistics.a.sent;
						statistics.clicked += message.statistics.a.clicked;
					}
					if (message.messages[1].templateId == templateId) {
						statistics.sent += message.statistics.b.sent;
						statistics.clicked += message.statistics.b.clicked;
					}
				} else if (message.messages[0].templateId == templateId) {
					statistics.sent += message.statistics.sent;
					statistics.clicked += message.statistics.clicked;
				}
			});
			statistics.ctr = ctr(statistics.clicked, statistics.sent);
			return statistics;
		},
		messages(state) {
			return state.messages.filter(
				(message) =>
					!message.automated &&
					message.status !== 'template' &&
					message.status !== 'automated' &&
					message.messages &&
					message.messages.length < 2
			);
		},
		templateMessages(state) {
			return state.messages.filter((message) => message.status === 'template');
		},
		templates(state) {
			return state.templates;
		},
		lookerConfiguration(state) {
			return state.lookerConfiguration;
		},
		abTests(state) {
			return state.messages.filter(
				(message) => message.messages && message.messages.length === 2 && !message.automated
			);
		},
		automatedABTests(state) {
			return state.messages.filter(
				(message) => message.messages && message.messages.length === 2 && message.automated
			);
		},
		automatedMessages(state) {
			return state.messages.filter((message) => message.automated && message.messages.length != 2);
		},
		activeAutomatedMessages(state) {
			return state.messages.filter((message) => message.automated && message.status != 'ended');
		},
		isStaging(state) {
			return state.isStaging;
		},
		isLoading: state => type => state.loading.entities[type] || false,
		isInitialized: state => state.loading.initialized,
		permissionTypes: state => state.permissionTypes,
		// Delegate to module getters for app
		activeApp: (state, getters) => getters['app/activeApp'],
		apps: (state, getters) => getters['app/apps'],
		appsDetails: (state, getters) => getters['app/appsDetails'],
		// Delegate to module getters for retention
		retentionMessages: (state, getters) => getters['retention/messages'],
		retentionMessageById: (state, getters) => (id) => getters['retention/messageById'](id),
		// Delegate to module getters for user/permissions
		user: (state, getters) => getters['user/user'],
		authToken: (state, getters) => getters['user/authToken'],
		isAuthenticated: (state, getters) => getters['user/isAuthenticated'],
		permissions: (state, getters) => getters['user/permissions'],
		userPermissions: (state, getters) => getters['user/userPermissions'],
		permissionsLoaded: (state, getters) => getters['user/permissionsLoaded'],
		userCapabilities: (state, getters) => getters['user/userCapabilities'],
		canEditExperiments: (state, getters) => getters['user/canEditExperiments'],
		canEditOngoingExperiments: (state, getters) => getters['user/canEditOngoingExperiments'],
		canSendPushNotifications: (state, getters) => getters['user/canSendPushNotifications'],
		canSchedulePushNotifications: (state, getters) => getters['user/canSchedulePushNotifications'],
		canCreateDraftMessages: (state, getters) => getters['user/canCreateDraftMessages'],
		canDeleteExperiments: (state, getters) => getters['user/canDeleteExperiments'],
		canActivateExperiments: (state, getters) => getters['user/canActivateExperiments'],
		canViewISLKeys: (state, getters) => getters['user/canViewISLKeys'],
		canCreateISLKeys: (state, getters) => getters['user/canCreateISLKeys'],
		canEditISLKeys: (state, getters) => getters['user/canEditISLKeys'],
		canDeleteISLKeys: (state, getters) => getters['user/canDeleteISLKeys'],
		userIsAdmin: (state, getters) => getters['user/userIsAdmin'],
		canViewExperiments: (state, getters) => getters['user/canViewExperiments'],
		authContext: (state, getters) => getters['user/authContext'],
		canViewRetentionMessages: (state, getters) => getters['user/canViewRetentionMessages'],
		canCreateRetentionMessages: (state, getters) => getters['user/canCreateRetentionMessages'],
		canEditRetentionMessages: (state, getters) => getters['user/canEditRetentionMessages'],
		canDeleteRetentionMessages: (state, getters) => getters['user/canDeleteRetentionMessages'],
		canApproveRetentionMessages: (state, getters) => getters['user/canApproveRetentionMessages'],
		userCheckCache: state => state.userCheckCache
	}
});

export default store;
